import { useCallback, useMemo } from 'react'
import { CheckIcon, XIcon } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'

import {
	ANCHOR_STATUS,
	useGeneratedAnchorFamiliesQuery,
} from '@/entities/anchor'

import { Operators } from '@/shared/components/query-builder'
import { Button } from '@/shared/components/ui/button'
import { confirm } from '@/shared/components/ui/confirmer'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Spinner } from '@/shared/components/ui/spinner'
import { useLocationQuery } from '@/shared/hooks/useLocationQuery'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import { useApproveAnchorFamilies } from '../lib/useApproveAnchorFamilies'
import { useRejectAnchorFamilies } from '../lib/useRejectAnchorFamilies'
import { useApprovalPageContext } from './ApprovalPageProvider'

export function AnchorFamiliesBulkDecision() {
	const { t } = useTranslation()

	const { selected, isDataFetching, isMutationPending } =
		useApprovalPageContext()
	const [{ filters }] = useLocationQuery()

	const { handleApproveAnchorFamilies, isPending: isApprovePending } =
		useApproveAnchorFamilies()
	const { handleRejectAnchorFamilies, isPending: isRejectPending } =
		useRejectAnchorFamilies()

	const hasSelectedFamilies = !!selected.length
	const actionFilters = useMemo(
		() =>
			hasSelectedFamilies
				? [{ name: 'id', operation: Operators.IN as const, value: selected }]
				: [
						...(filters || []),
						{
							name: 'status',
							operation: Operators.IS as const,
							value: ANCHOR_STATUS.REVIEW,
						},
					],
		[selected, filters, hasSelectedFamilies]
	)
	const familiesQuery = useGeneratedAnchorFamiliesQuery({
		limit: 1,
		offset: 0,
		filters: actionFilters,
	})
	const count = familiesQuery.data?.total || 0
	const handleApprove = useCallback(async () => {
		const confirmed = await confirm({
			title: t('anchoring.multiple_approve.confirm.title', {
				defaultValue: 'Multiple families approval',
			}),
			description: (
				<Trans
					i18nKey='anchoring.multiple_approve.confirm.description'
					defaults='You are about to approve <strong>{count}</strong> families. <br/> This action cannot be undone.'
					values={{ count: formatNumber(count, NumberFormats.Integer) }}
					components={{ strong: <strong className='text-foreground' /> }}
				/>
			),
		})
		if (confirmed) {
			await handleApproveAnchorFamilies(actionFilters)
		}
	}, [handleApproveAnchorFamilies, actionFilters, t, count])
	const handleReject = useCallback(async () => {
		const confirmed = await confirm({
			title: t('anchoring.multiple_reject.confirm.title', {
				defaultValue: 'Multiple families rejection',
			}),
			description: (
				<Trans
					i18nKey='anchoring.multiple_reject.confirm.description'
					defaults='You are about to reject <strong>{count}</strong> families. <br/> This action cannot be undone.'
					values={{ count: formatNumber(count, NumberFormats.Integer) }}
					components={{ strong: <strong className='text-foreground' /> }}
				/>
			),
		})
		if (confirmed) {
			await handleRejectAnchorFamilies(actionFilters)
		}
	}, [handleRejectAnchorFamilies, actionFilters, t, count])

	if (isDataFetching) {
		return (
			<div className='flex gap-2'>
				<Skeleton className='h-8 w-32 animate-pulse rounded-lg' />
				<Skeleton className='h-8 w-32 animate-pulse rounded-lg' />
			</div>
		)
	}
	const isDisabled =
		isMutationPending || familiesQuery.isFetching || count === 0

	return (
		<div className='flex gap-2'>
			<Button
				onClick={handleApprove}
				data-testid='approve-button'
				disabled={isDisabled}
				//fix when a success variant is implemented
				className='dark:text-success-200 bg-success text-white hover:bg-success/90 hover:text-white dark:bg-success/90 dark:hover:bg-success/80'
			>
				{isApprovePending ? <Spinner /> : <CheckIcon />}
				{t('app.approve')}
				{familiesQuery.isFetching ? (
					<Spinner />
				) : (
					<span className='tabular-nums'>
						({formatNumber(count, NumberFormats.Integer)})
					</span>
				)}
			</Button>
			<Button
				data-testid='reject-button'
				onClick={handleReject}
				variant='destructive'
				disabled={isDisabled}
			>
				{isRejectPending ? <Spinner /> : <XIcon />}
				{t('app.reject', { defaultValue: 'Reject' })}{' '}
				{familiesQuery.isFetching ? (
					<Spinner />
				) : (
					<span className='tabular-nums'>
						({formatNumber(count, NumberFormats.Integer)})
					</span>
				)}
			</Button>
		</div>
	)
}
